import type {
  ApprovalItem,
  CommunicationLog,
  AuditEvent,
  Holder,
  LedgerEntry,
  RegistryDocument,
  RegistryState,
} from '../data/types';
import { DEMO_TODAY, nowStamp } from '../lib/format';
import { newId } from '../lib/random';

export function actorName(state: RegistryState): string {
  return state.users.find((user) => user.id === state.currentUserId)?.name ?? 'System';
}

export function withAudit(
  state: RegistryState,
  action: string,
  target: string,
  detail: string,
  actor = actorName(state),
): RegistryState {
  const event: AuditEvent = { id: newId('au'), at: nowStamp(), actor, action, target, detail };
  return { ...state, audit: [event, ...state.audit] };
}

export function appendLedger(
  state: RegistryState,
  entries: Omit<LedgerEntry, 'seq' | 'id' | 'postedAt'>[],
): RegistryState {
  let seq = state.ledger.reduce((max, entry) => Math.max(max, entry.seq), 0);
  const posted = entries.map((item) => {
    seq += 1;
    return { ...item, id: newId('le'), seq, postedAt: nowStamp() };
  });
  return { ...state, ledger: [...state.ledger, ...posted] };
}

export function addApproval(
  state: RegistryState,
  item: Omit<ApprovalItem, 'id' | 'requestedAt' | 'status' | 'requestedBy'> & {
    requestedBy?: string;
  },
): RegistryState {
  const approval: ApprovalItem = {
    ...item,
    id: newId('ap'),
    requestedBy: item.requestedBy ?? actorName(state),
    requestedAt: nowStamp(),
    status: 'PENDING',
  };
  return { ...state, approvals: [approval, ...state.approvals] };
}

export function addDocuments(
  state: RegistryState,
  documents: Omit<RegistryDocument, 'id'>[],
): RegistryState {
  return {
    ...state,
    documents: [...documents.map((doc) => ({ ...doc, id: newId('doc') })), ...state.documents],
  };
}

export function updateHolder(
  state: RegistryState,
  holderId: string,
  patch: (holder: Holder) => Partial<Holder>,
): RegistryState {
  return {
    ...state,
    holders: state.holders.map((holder) =>
      holder.id === holderId ? { ...holder, ...patch(holder) } : holder,
    ),
  };
}

export function completeTasks(
  state: RegistryState,
  predicate: (task: RegistryState['tasks'][number]) => boolean,
): RegistryState {
  return {
    ...state,
    tasks: state.tasks.map((task) =>
      task.status === 'OPEN' && predicate(task) ? { ...task, status: 'DONE' } : task,
    ),
  };
}

export function fundCode(state: RegistryState, fundId?: string): string {
  return state.funds.find((fund) => fund.id === fundId)?.code ?? '';
}

export function holderName(state: RegistryState, holderId?: string): string {
  return state.holders.find((holder) => holder.id === holderId)?.name ?? 'Unknown holder';
}

/** The registry record for a platform investment entity, if it holds anything yet. */
export function holderIdForEntity(
  state: RegistryState,
  investmentEntityId: string,
): string | undefined {
  return state.holders.find((holder) => holder.investmentEntityId === investmentEntityId)?.id;
}

export const today = DEMO_TODAY;

export function addCommunication(
  state: RegistryState,
  log: Omit<CommunicationLog, 'id' | 'at' | 'sentBy' | 'delivered' | 'opened' | 'bounced'> &
    Partial<Pick<CommunicationLog, 'sentBy'>>,
): RegistryState {
  const entry: CommunicationLog = {
    ...log,
    id: newId('cm'),
    at: nowStamp(),
    sentBy: log.sentBy ?? actorName(state),
    delivered: log.holderIds.length,
    opened: 0,
    bounced: 0,
  };
  return { ...state, communications: [entry, ...state.communications] };
}

export function raiseHeldPayments(
  state: RegistryState,
  fundId: string,
  eventLabel: string,
  payments: { holderId: string; amount: number }[],
): RegistryState {
  const held = payments.filter(
    (payment) =>
      payment.amount > 0 &&
      !state.holders.find((holder) => holder.id === payment.holderId)?.bank.verified,
  );
  if (held.length === 0) return state;
  return {
    ...state,
    paymentExceptions: [
      ...held.map((payment) => ({
        id: newId('pe'),
        fundId,
        holderId: payment.holderId,
        eventLabel,
        amount: payment.amount,
        reason: 'HELD_UNVERIFIED_BANK' as const,
        raisedAt: today,
        status: 'OPEN' as const,
      })),
      ...state.paymentExceptions,
    ],
  };
}
